import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Users, Clock, AlertCircle, ArrowRight, Volume2, VolumeX, Move, Wifi, Zap, Edit3, Check, X, Crown, Sparkles, AlertTriangle, Megaphone } from "lucide-react";
import EnergizerOverlay from "../components/EnergizerOverlay";
import { BuzzerButton, BuzzerWaiting } from "../components/BuzzerButton";
import { ParticipantScoreboard } from "../components/Scoreboard";
import FinalScoresReveal from "../components/FinalScoresReveal";
import StationQA from "../components/StationQA";
import { ModeToggle } from "../context/ThemeContext";
import { TeamAvatar, AvatarSelector } from "../components/TeamAvatar";
import { useServerSyncedTimer } from "../hooks/useServerSyncedTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fun waiting messages to keep participants entertained
const WAITING_MESSAGES = [
  { text: "🎯 Get ready to bring your A-game!", subtext: "Champions are made, not born" },
  { text: "🧠 Warm up those brain cells!", subtext: "Genius mode: activated" },
  { text: "⚡ Energy check: Maximum power!", subtext: "Let's crush this" },
  { text: "🏆 Today's winner is in this room!", subtext: "Is it you?" },
  { text: "🎪 The fun is about to begin!", subtext: "Stay tuned..." },
  { text: "🚀 Launch sequence initiated!", subtext: "T-minus awesome" },
  { text: "🎭 Plot twist: You're going to win!", subtext: "Probably..." },
  { text: "🌟 Stardom awaits!", subtext: "Shine bright" },
  { text: "🎯 Focus mode: ON", subtext: "Eye of the tiger" },
  { text: "🔥 Bringing the heat today!", subtext: "Too hot to handle" }
];

// Get device ID from localStorage
const getDeviceId = () => {
  let deviceId = localStorage.getItem("carousel_device_id");
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("carousel_device_id", deviceId);
  }
  return deviceId;
};

const ParticipantView = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const pollIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const lastRotationRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showEnergizer, setShowEnergizer] = useState(false);
  const [showMoveAlert, setShowMoveAlert] = useState(false);
  // Synced timer derived from status.server_time + status.timer_end_time
  const timer = useServerSyncedTimer(status);
  const timeRemaining = timer.active ? timer.seconds : null;
  const prevTimerSecondsRef = useRef(null);

  // Announcement modal state
  const [seenAnnouncementId, setSeenAnnouncementId] = useState(() => localStorage.getItem("carousel_seen_announcement") || "");
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  
  // Team name editing state
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const [teamNameInput, setTeamNameInput] = useState("");
  
  // Team avatar editing state
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  
  // Mute state - persisted to localStorage
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem("participant_muted") === "true";
  });
  
  // Fun message rotation
  const [messageIndex, setMessageIndex] = useState(() => Math.floor(Math.random() * WAITING_MESSAGES.length));
  
  // Rotate fun messages every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % WAITING_MESSAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Toggle mute function
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    localStorage.setItem("participant_muted", newMuted.toString());
    toast.success(newMuted ? "🔇 Sounds muted" : "🔊 Sounds enabled");
  };

  // Timer audio
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdXuDfm9tc4WKgHBqdYuSg3RseY+XhnNqepqghHJofKKngXRmeKqvf3RieayygXFbcq2yf3BYba2wf3FYaqquf3NZZ6aqf3VcZqGmf3hfZpqif3piZpKcgH5mZ4uXgIFqaYaRgIRtaoKNgIZwa3+KgIhzbHyHgIp1bnqEgIx4cHiCgI56cnaAgI98dHV+gJB+dXR9gJGAd3N8gJKCeHJ7gJOEenF6gJSGenB5gJWIe3B4gJaKfHB3gJeMfXB3gJiOfrV3dwA=");
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Play rotation sound
  const playRotationSound = useCallback(() => {
    if (isMuted) return;
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [isMuted]);

  // Show announcement modal when a new announcement arrives
  useEffect(() => {
    const a = status?.announcement;
    if (a && a.id !== seenAnnouncementId) {
      setShowAnnouncement(true);
      if (!isMuted && audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    }
  }, [status?.announcement?.id, seenAnnouncementId, isMuted]);

  const dismissAnnouncement = useCallback(() => {
    const a = status?.announcement;
    if (a) {
      localStorage.setItem("carousel_seen_announcement", a.id);
      setSeenAnnouncementId(a.id);
    }
    setShowAnnouncement(false);
  }, [status?.announcement?.id]);

  // Update team name
  const handleUpdateTeamName = useCallback(async (newName) => {
    if (!status?.group?.number) return;
    try {
      await axios.patch(`${API}/sessions/${sessionId}/groups/${status.group.number}/name?name=${encodeURIComponent(newName)}`);
      toast.success(`Team renamed to "${newName || `Team ${status.group.number}`}"`);
      setIsEditingTeamName(false);
      setTeamNameInput("");
    } catch (error) {
      toast.error("Failed to update team name");
    }
  }, [sessionId, status?.group?.number]);

  // Get team display name
  const getTeamDisplayName = (group) => {
    return group?.name || `Team ${group?.number}`;
  };

  // Fetch participant status - this is the core polling function
  const fetchStatus = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      const response = await axios.get(`${API}/participants/${deviceId}/session/${sessionId}`);
      const newData = response.data;
      
      // Check if rotation happened - compare with ref to avoid stale closure
      if (lastRotationRef.current !== null && newData.rotation_index > lastRotationRef.current) {
        console.log(`Rotation detected! ${lastRotationRef.current} -> ${newData.rotation_index}`);
        setShowMoveAlert(true);
        playRotationSound();
        setTimeout(() => setShowMoveAlert(false), 5000);
      }
      lastRotationRef.current = newData.rotation_index;
      
      // Check energizer state changes
      setStatus(prevStatus => {
        if (newData.energizer_revealed && (!prevStatus || !prevStatus.energizer_revealed)) {
          setShowEnergizer(true);
        }
        if (!newData.energizer_revealed && prevStatus?.energizer_revealed) {
          setShowEnergizer(false);
        }
        return newData;  // Always return new data to trigger re-render
      });
      
      setLoadError(null);

      setIsLoading(false);
      return true;
    } catch (error) {
      console.error("Error fetching status:", error);
      if (error.response?.status === 404) {
        setLoadError("not_joined");
      } else {
        // Don't set error on transient failures, keep polling
        console.log("Transient error, continuing to poll...");
      }
      setIsLoading(false);
      return false;
    }
  }, [sessionId, playRotationSound]);

  // Polling effect - runs continuously
  useEffect(() => {
    let isActive = true;
    
    // Initial fetch
    fetchStatus();
    
    // Start polling every 2 seconds for real-time updates
    pollIntervalRef.current = setInterval(() => {
      if (isActive) {
        fetchStatus();
      }
    }, 2000);
    
    return () => {
      isActive = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchStatus]);

  // Timer-end audio cue (fires once when synced timer transitions positive -> 0)
  useEffect(() => {
    const prev = prevTimerSecondsRef.current;
    const curr = timer.active ? timer.seconds : null;
    if (prev !== null && prev > 0 && curr === 0) {
      playRotationSound();
    }
    prevTimerSecondsRef.current = curr;
  }, [timer.active, timer.seconds, playRotationSound]);

  // Update team avatar
  const handleUpdateTeamAvatar = async (avatarId) => {
    try {
      await axios.patch(`${API}/sessions/${sessionId}/groups/${group?.number}/avatar?avatar_id=${encodeURIComponent(avatarId)}`);
      toast.success("Avatar updated! 🎉");
      setIsEditingAvatar(false);
      fetchSession();
    } catch (error) {
      toast.error("Failed to update avatar");
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Team lead claim/unclaim
  const handleClaimLead = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/teams/${status?.group?.id}/claim-lead?device_id=${getDeviceId()}`);
      toast.success("You are now the Team Lead!");
      fetchStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to claim lead role";
      toast.error(msg);
    }
  };

  const handleUnclaimLead = async () => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/teams/${status?.group?.id}/unclaim-lead?device_id=${getDeviceId()}`);
      toast.success("Team lead role removed");
      fetchStatus();
    } catch {
      toast.error("Failed to remove lead role");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#E2D9F3]">Loading workshop...</p>
        </div>
      </div>
    );
  }

  // Not joined - redirect to join page
  if (loadError === "not_joined") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#FFC400] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Join Required</h2>
          <p className="text-[#E2D9F3] mb-6">You need to join this session first</p>
          <button
            onClick={() => navigate("/join")}
            className="btn-primary-gradient control-btn"
          >
            Go to Join Page
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#FF1744] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Connection Error</h2>
          <p className="text-[#E2D9F3] mb-6">Unable to load session</p>
          <button
            onClick={() => {
              setIsLoading(true);
              setLoadError(null);
              fetchStatus();
            }}
            className="btn-primary-gradient control-btn mr-3"
          >
            Retry
          </button>
          <button
            onClick={() => navigate("/join")}
            className="btn-ghost control-btn"
          >
            Join Different Session
          </button>
        </div>
      </div>
    );
  }

  const { group, current_station, next_station, session_status, session_mode, presentation_mode, energizer_revealed, signal, show_questions, buzzer_active, team_buzzed, buzz_position, show_scoreboard, show_final_scores, scoreboard, current_round, is_team_lead, team_has_lead, team_lead_name, has_team_questions } = status;

  // Determine what features are available based on session mode
  const isCarouselMode = session_mode === "carousel" || session_mode === "hybrid";

  // Announcement modal (rendered in lobby + live views)
  const announcementModal = (showAnnouncement && status?.announcement) ? (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm"
      data-testid="announcement-modal"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="glass-heavy rounded-3xl p-6 md:p-8 max-w-md w-full text-center border border-[#E91E8C]/30"
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}>
          <Megaphone className="w-7 h-7 text-white" />
        </div>
        <p className="text-xs uppercase tracking-wider text-[#E91E8C] mb-2 font-semibold">
          Announcement{status.announcement.target_group_id ? " · Your Team" : ""}
        </p>
        <p className="text-white text-lg md:text-xl leading-relaxed mb-6 whitespace-pre-wrap" data-testid="announcement-message">
          {status.announcement.message}
        </p>
        <button onClick={dismissAnnouncement} className="btn-primary-gradient control-btn w-full" data-testid="announcement-dismiss-btn">
          Got it
        </button>
      </motion.div>
    </motion.div>
  ) : null;
  const isQuizMode = session_mode === "quiz" || session_mode === "hybrid";
  const hasStations = session_mode === "carousel" || session_mode === "hybrid";
  
  // Determine what to show based on presentation mode (facilitator's choice)
  // In hybrid mode, "lobby" should NOT show stations — only explicit "carousel" mode should.
  // In carousel-only mode, "lobby" still shows stations since that's the only content.
  const showStationsNow = presentation_mode === "carousel" || (presentation_mode === "lobby" && session_mode === "carousel");
  const showQuizNow = presentation_mode === "buzzer" || presentation_mode === "question" || presentation_mode === "results";
  const showScoreboardNow = presentation_mode === "scoreboard";
  
  // In hybrid mode, show a neutral waiting state when facilitator hasn't picked stations or quiz yet
  const isHybridWaiting = session_mode === "hybrid" && (presentation_mode === "lobby" || presentation_mode === "final");

  // Final Scores Reveal - Full screen overlay
  if (show_final_scores && scoreboard) {
    return <FinalScoresReveal scoreboard={scoreboard} />;
  }

  // Session ended view
  if (session_status === "ended") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Workshop Complete!</h2>
          <p className="text-[#E2D9F3] text-lg mb-6">Thank you for participating</p>
          <button
            onClick={() => {
              localStorage.removeItem("carousel_session");
              navigate("/");
            }}
            className="btn-ghost control-btn"
          >
            Return Home
          </button>
        </motion.div>
      </div>
    );
  }

  // Waiting for session to start (lobby state)
  if (session_status === "draft" || session_status === "lobby") {
    const currentMessage = WAITING_MESSAGES[messageIndex];
    
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 relative">
        {announcementModal}
        {/* Controls in corner */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <ModeToggle />
          <button
            onClick={toggleMute}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title={isMuted ? "Unmute sounds" : "Mute sounds"}
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-[#FF1744]" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center mx-auto mb-6 energizer-pulse">
            <Clock className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Waiting to Start</h2>
          
          {/* Fun rotating message */}
          <AnimatePresence mode="wait">
            <motion.div
              key={messageIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              <p className="text-2xl text-white font-semibold mb-1">{currentMessage.text}</p>
              <p className="text-[#E2D9F3]">{currentMessage.subtext}</p>
            </motion.div>
          </AnimatePresence>
          
          <div className="glass-heavy rounded-2xl p-6">
            <p className="text-sm text-[#E2D9F3] mb-2">You've been assigned to</p>
            {isEditingTeamName ? (
              <div className="flex items-center justify-center gap-2">
                <input
                  type="text"
                  value={teamNameInput}
                  onChange={(e) => setTeamNameInput(e.target.value)}
                  placeholder={`Team ${group?.number}`}
                  className="bg-white/10 text-white text-xl font-bold rounded-lg px-4 py-2 outline-none text-center w-40"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateTeamName(teamNameInput);
                    } else if (e.key === 'Escape') {
                      setIsEditingTeamName(false);
                      setTeamNameInput("");
                    }
                  }}
                />
                <button
                  onClick={() => handleUpdateTeamName(teamNameInput)}
                  className="text-[#00E676] hover:text-white transition-colors p-2"
                  title="Save"
                >
                  <Check className="w-6 h-6" />
                </button>
                <button
                  onClick={() => {
                    setIsEditingTeamName(false);
                    setTeamNameInput("");
                  }}
                  className="text-[#FF1744] hover:text-white transition-colors p-2"
                  title="Cancel"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            ) : (
              <div 
                className="group-badge text-2xl cursor-pointer hover:bg-white/20 transition-colors"
                onClick={() => {
                  setIsEditingTeamName(true);
                  setTeamNameInput(group?.name || "");
                }}
                title="Click to edit team name"
              >
                {getTeamDisplayName(group)}
                <Edit3 className="w-5 h-5 ml-2 opacity-60" />
              </div>
            )}
            <p className="text-xs text-[#6B5E7C] mt-3">Tap to change your team name</p>
            
            {/* Team Lead Claim */}
            <div className="mt-4 pt-4 border-t border-white/10">
              {is_team_lead ? (
                <div className="flex items-center justify-center gap-2">
                  <span data-testid="team-lead-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold" style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}>
                    <Crown className="w-4 h-4" /> Team Lead
                  </span>
                  <button onClick={handleUnclaimLead} data-testid="unclaim-lead-btn" className="text-xs text-[#6B5E7C] hover:text-[#FF1744] underline">
                    Step down
                  </button>
                </div>
              ) : team_has_lead ? (
                <p className="text-sm text-[#E2D9F3]">
                  <span className="font-semibold">{team_lead_name || "Someone"}</span> is your Team Lead
                </p>
              ) : has_team_questions ? (
                <div className="rounded-xl p-3 bg-[#FF9800]/15 border border-[#FF9800]/30 text-center" data-testid="needs-lead-warning">
                  <p className="text-sm font-medium text-[#FFB74D] flex items-center justify-center gap-1.5 mb-2">
                    <AlertTriangle className="w-4 h-4" /> Your team needs a Team Lead
                  </p>
                  <button
                    onClick={handleClaimLead}
                    className="px-4 py-2 rounded-xl btn-primary-gradient text-white text-sm font-semibold"
                    data-testid="claim-lead-btn"
                  >
                    <Crown className="w-4 h-4 inline mr-1.5" />
                    Tap to volunteer
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleClaimLead}
                  className="px-4 py-2 rounded-xl bg-white/5 border border-white/20 text-white text-sm font-medium hover:bg-white/10 hover:border-white/40 transition-all"
                  data-testid="claim-lead-btn"
                >
                  <Crown className="w-4 h-4 inline mr-1.5" />
                  I'm the Team Lead
                </button>
              )}
            </div>
          </div>

          {/* Energizer signal (icebreaker) - shown when facilitator reveals signals before start */}
          {energizer_revealed && signal && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-heavy rounded-2xl p-6 mt-4 border border-[#CCFF00]/30"
              data-testid="lobby-signal-card"
            >
              <p className="text-xs uppercase tracking-wider text-[#CCFF00] mb-3 flex items-center justify-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Your Team Signal
              </p>
              <div className="text-6xl mb-2" data-testid="lobby-signal-icon">{signal.icon}</div>
              <p className="text-2xl font-bold text-white" data-testid="lobby-signal-name">{signal.name}</p>
              <p className="text-sm text-[#E2D9F3] mt-2">
                {signal.instruction ? signal.instruction : "Make this sound to find your teammates!"}
              </p>
              {signal.alternative && (
                <p className="text-xs text-[#6B5E7C] mt-2">Quiet option: {signal.alternative}</p>
              )}
            </motion.div>
          )}

          <div className="mt-6 flex items-center justify-center gap-2 text-sm">
            <Wifi className="w-4 h-4 text-[#00E676]" />
            <span className="text-[#00E676]">Live updates active</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 pb-24 relative">
      {announcementModal}
      {/* Energizer Overlay */}
      <AnimatePresence>
        {showEnergizer && signal && (
          <EnergizerOverlay
            signal={signal}
            groupNumber={group?.number}
            onClose={() => setShowEnergizer(false)}
          />
        )}
      </AnimatePresence>

      {/* Move Alert Overlay */}
      <AnimatePresence>
        {showMoveAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="text-center p-8"
            >
              <div className="w-24 h-24 rounded-full bg-[#CCFF00] flex items-center justify-center mx-auto mb-6 animate-bounce">
                <ArrowRight className="w-12 h-12 text-black" />
              </div>
              <h2 className="text-4xl font-bold text-[#CCFF00] mb-2">MOVE NOW!</h2>
              <p className="text-white text-xl">
                Go to <span className="font-bold">{current_station?.name}</span>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls in corner */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-2">
        <ModeToggle />
        <button
          onClick={toggleMute}
          className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          title={isMuted ? "Unmute sounds" : "Mute sounds"}
        >
          {isMuted ? <VolumeX className="w-5 h-5 text-[#FF1744]" /> : <Volume2 className="w-5 h-5 text-white" />}
        </button>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        {isEditingTeamName ? (
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
            <Users className="w-5 h-5 text-[#E91E8C]" />
            <input
              type="text"
              value={teamNameInput}
              onChange={(e) => setTeamNameInput(e.target.value)}
              placeholder={`Team ${group?.number}`}
              className="bg-transparent text-white text-lg font-semibold outline-none w-32"
              autoFocus
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateTeamName(teamNameInput);
                } else if (e.key === 'Escape') {
                  setIsEditingTeamName(false);
                  setTeamNameInput("");
                }
              }}
            />
            <button
              onClick={() => handleUpdateTeamName(teamNameInput)}
              className="text-[#00E676] hover:text-white transition-colors p-1"
              title="Save"
            >
              <Check className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setIsEditingTeamName(false);
                setTeamNameInput("");
              }}
              className="text-[#FF1744] hover:text-white transition-colors p-1"
              title="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 mb-4">
            {/* Team Avatar - clickable to edit */}
            <div className="relative">
              <TeamAvatar 
                teamNumber={group?.number || 1} 
                avatarId={group?.avatar_id}
                size="lg"
                onClick={() => setIsEditingAvatar(!isEditingAvatar)}
              />
              {isEditingAvatar && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50">
                  <AvatarSelector
                    currentAvatarId={group?.avatar_id}
                    onSelect={(avatarId) => handleUpdateTeamAvatar(avatarId)}
                    onClose={() => setIsEditingAvatar(false)}
                  />
                </div>
              )}
            </div>
            
            <div 
              className="group-badge inline-flex cursor-pointer hover:bg-white/20 transition-colors" 
              data-testid="group-badge"
              onClick={() => {
                setIsEditingTeamName(true);
                setTeamNameInput(group?.name || "");
              }}
              title="Click to edit team name"
            >
              {getTeamDisplayName(group)}
              <Edit3 className="w-4 h-4 ml-2 opacity-60" />
            </div>
          </div>
        )}
        
        {/* Timer */}
        {timeRemaining !== null && timeRemaining > 0 && (
          <div className="mt-4" data-testid="timer-display">
            <p className="text-[#E2D9F3] text-sm mb-1">Time Remaining</p>
            <p className={`timer-huge ${timeRemaining <= 30 ? "text-[#FF1744]" : "gradient-text"}`}>
              {timer.mmss}
            </p>
          </div>
        )}
      </motion.div>

      {/* Buzzer Section - Shows when buzzer is active */}
      {buzzer_active && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/95 backdrop-blur-sm"
        >
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center justify-center gap-2 text-[#CCFF00] mb-2">
                <Zap className="w-6 h-6" />
                <span className="text-lg font-semibold">QUIZ BUZZER</span>
                <Zap className="w-6 h-6" />
              </div>
              <p className="text-[#E2D9F3]">Be the first team to buzz!</p>
            </motion.div>
            
            <BuzzerButton
              sessionId={sessionId}
              deviceId={getDeviceId()}
              groupNumber={group?.number}
              teamBuzzed={team_buzzed}
              buzzPosition={buzz_position}
            />
          </div>
        </motion.div>
      )}

      {/* Current Station Card - Show when in station/carousel mode or lobby */}
      {hasStations && current_station && showStationsNow && (
        <motion.div
          key={current_station?.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
          data-testid="station-card"
        >
          {/* CURRENT STATION - Large and prominent */}
          <div className="station-card mb-4">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}
              >
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#E2D9F3] uppercase tracking-wider font-medium">You Are Here</p>
                <h2 className="text-3xl md:text-4xl font-bold gradient-text">{current_station?.name}</h2>
              </div>
            </div>

            {/* Content based on content_type */}
            {(() => {
              const contentType = current_station?.content_type || "questions";
              const hasQuestions = current_station?.questions?.length > 0;
              const hasInstructions = current_station?.instructions?.trim();
              
              // Questions type (may also include instructions shown first)
              if (contentType === "questions") {
                // Show if there are instructions OR questions are enabled
                const shouldShow = hasInstructions || (hasQuestions && show_questions !== false);
                if (shouldShow) {
                  const stationHasTeamQ = (current_station.questions || []).some(q => q.scope === "team" || (q.parts || []).some(p => p.scope === "team"));
                  return (
                    <>
                      {stationHasTeamQ && !team_has_lead && !is_team_lead && (
                        <div className="rounded-xl p-3 mb-3 bg-[#FF9800]/15 border border-[#FF9800]/30 flex items-center justify-between gap-3" data-testid="station-needs-lead-warning">
                          <p className="text-sm font-medium text-[#FFB74D] flex items-center gap-1.5">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Your team has no Team Lead
                          </p>
                          <button
                            onClick={handleClaimLead}
                            className="px-3 py-1.5 rounded-lg btn-primary-gradient text-white text-xs font-semibold whitespace-nowrap"
                            data-testid="station-claim-lead-btn"
                          >
                            <Crown className="w-3.5 h-3.5 inline mr-1" /> I'll lead
                          </button>
                        </div>
                      )}
                      {stationHasTeamQ && is_team_lead && (
                        <div className="mb-3 flex items-center justify-center gap-1.5 text-xs font-medium text-[#FFC400]" data-testid="station-you-are-lead">
                          <Crown className="w-3.5 h-3.5" /> You are answering team questions as Team Lead
                        </div>
                      )}
                      <StationQA
                        sessionId={sessionId}
                        stationId={current_station.id}
                        questions={show_questions !== false ? current_station.questions : []}
                        deviceId={getDeviceId()}
                        instructions={current_station.instructions}
                        isTeamLead={is_team_lead}
                      />
                    </>
                  );
                } else {
                  return (
                    <div className="text-center py-4 text-[#E2D9F3]">
                      <p className="text-lg">Questions coming soon...</p>
                      <p className="text-sm text-[#6B5E7C] mt-1">Follow your facilitator</p>
                    </div>
                  );
                }
              }
              
              // Instructions type - show instructions
              if (contentType === "instructions") {
                if (current_station?.instructions) {
                  return (
                    <div className="glass-light rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        📋 Instructions
                      </h3>
                      <div className="text-[#E2D9F3] text-base leading-relaxed whitespace-pre-wrap">
                        {current_station.instructions}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-4 text-[#E2D9F3]">
                      <p className="text-lg">Instructions coming soon...</p>
                      <p className="text-sm text-[#6B5E7C] mt-1">Follow your facilitator</p>
                    </div>
                  );
                }
              }
              
              // Open text type - show the text
              if (contentType === "open_text") {
                if (current_station?.open_text) {
                  return (
                    <div className="glass-light rounded-xl p-6">
                      <div className="text-[#E2D9F3] text-base leading-relaxed whitespace-pre-wrap">
                        {current_station.open_text}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-4 text-[#E2D9F3]">
                      <p className="text-lg">Content coming soon...</p>
                      <p className="text-sm text-[#6B5E7C] mt-1">Follow your facilitator</p>
                    </div>
                  );
                }
              }
              
              // Name only type - engage message
              if (contentType === "name_only") {
                return (
                  <div className="text-center py-6 text-[#E2D9F3]">
                    <p className="text-xl mb-2">🎯 Engage with your team</p>
                    <p className="text-sm text-[#6B5E7C]">Follow your facilitator's instructions</p>
                  </div>
                );
              }
              
              // Fallback - should rarely happen
              return (
                <div className="text-center py-4 text-[#E2D9F3]">
                  <p className="text-lg">Follow your facilitator</p>
                </div>
              );
            })()}
          </div>

          {/* NEXT STATION - Smaller preview */}
          {next_station && next_station.id !== current_station?.id && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="glass-light rounded-xl p-4 border border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-[#E2D9F3]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#6B5E7C] uppercase tracking-wider">Next Station</p>
                  <p className="text-lg font-semibold text-[#E2D9F3]">{next_station.name}</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Quiz Mode - Waiting state when buzzer is not active - only show in quiz presentation mode */}
      {isQuizMode && showQuizNow && !buzzer_active && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-8 text-center mb-6"
        >
          <Zap className="w-16 h-16 text-[#6B5E7C] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Quiz Mode</h2>
          <p className="text-[#E2D9F3]">
            Waiting for the facilitator to start the buzzer...
          </p>
          <p className="text-[#6B5E7C] text-sm mt-4">
            Get ready to buzz when the question is asked!
          </p>
        </motion.div>
      )}

      {/* Hybrid Waiting State - when facilitator hasn't chosen stations or quiz yet */}
      {isHybridWaiting && session_status === "live" && !buzzer_active && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-2xl p-8 text-center mb-6"
          data-testid="hybrid-waiting"
        >
          <Users className="w-16 h-16 text-[#6B5E7C] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Get Ready!</h2>
          <p className="text-[#E2D9F3]">
            Waiting for the facilitator to start the next activity...
          </p>
        </motion.div>
      )}

      {/* Scoreboard - Show when facilitator enables it or in scoreboard mode */}
      {(show_scoreboard || showScoreboardNow) && scoreboard && (
        <ParticipantScoreboard 
          scoreboard={scoreboard} 
          myTeamNumber={group?.number} 
        />
      )}

      {/* Bottom Status Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 glass-heavy border-t border-white/10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wifi className="w-4 h-4 text-[#00E676]" />
            <span className="text-sm text-[#E2D9F3]">
              {session_status === "live" 
                ? (showQuizNow ? "Quiz Active" : showStationsNow ? "Workshop Active" : "Session Active") 
                : "Paused"}
            </span>
          </div>
          
          {energizer_revealed && signal && (
            <button
              data-testid="show-signal-btn"
              onClick={() => setShowEnergizer(true)}
              className="flex items-center gap-2 text-sm text-[#E91E8C] hover:text-white transition-colors"
            >
              {signal.instruction ? <Move className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              Show Signal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantView;
